import { ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import CircularProgress from "@mui/material/CircularProgress";
import { ITableColumn } from "interfaces/Common";
import TableSortLabel from "components/TableSortLabel/TableSortLabel";
import { IBaseOrder, IOrderDetails, IOrderFilters } from "interfaces/Order";
import Loading from "components/Loading/Loading";
import NoTableItems from "components/Tables/NoTableItems/NoTableItems";
import {
  createUrlParams,
  formatDashDate,
  formatName,
} from "helpers/CommonHelper";
import OrderService from "services/OrderService";
import { getOrderStatus, isOrderScheduled } from "helpers/OrderHelper";
import useFetchTableData from "hooks/useFetchTableData";
import useSnackbar from "hooks/useSnackbar";
import { OrderCancelType, OrderFilterParams, OrderStatus } from "enums/Order";
import CreateEditOrderModal from "components/Modals/CreateEditOrderModal/CreateEditOrderModal";
import { URLRoutes } from "enums/Routing";
import tableStyles from "styles/shared/table.module.scss";
import { SensorStatus } from "enums/Sensor";
import { Apps } from "enums/Common";

interface OrdersTableProps {
  dontReplaceParams?: boolean;
  filters: IOrderFilters;
  onPageIncrement: () => void;
  onSortChange: (value: string) => void;
  refreshData: () => void;
  showPatientColumns?: boolean;
  noItemsMessage?: string;
}

const OrdersTable = ({
  dontReplaceParams,
  filters,
  onPageIncrement,
  onSortChange,
  refreshData,
  showPatientColumns,
  noItemsMessage = "No orders.",
}: OrdersTableProps) => {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();

  const searchFilter = filters[OrderFilterParams.OrderOrPatientName];

  const { snackbarComponent, setSnackbarError } = useSnackbar();
  const { data, loading, scrollableParentRef, lastElementRef } =
    useFetchTableData<IBaseOrder, IOrderFilters>({
      filters,
      onPageIncrement,
      fetchAPI: OrderService.getAll,
      onError: setSnackbarError,
    });

  const [orderToRenew, setOrderToRenew] = useState<IOrderDetails>();
  const [loadingToRenewId, setLoadingToRenewId] = useState<string>();

  const tableColumns = useMemo(
    (): ITableColumn[] => [
      { label: "Order ID", sortProperty: "order_num" },
      ...(showPatientColumns
        ? [
            { label: "Name", sortProperty: "patient_lname,patient_fname" },
            { label: "Date of Birth", sortProperty: "patient_dob" },
          ]
        : []),
      { label: "Ordering Clinician", sortProperty: "ordered_by_fname,ordered_by_lname" },
      { label: "Order Type" },
      { label: "Start Date", sortProperty: "start_date" },
      { label: "Status", sortProperty: "status,cancel_type" },
      { label: "Sensor Status" },
      { label: "" },
    ],
    [showPatientColumns]
  );

  useEffect(() => {
    if (dontReplaceParams) return;

    const {
      page,
      page_size,
      _total_count,
      _refresh,
      patientId,
      // push only props that can be filtered
      ...urlParams
    } = filters;

    setSearchParams(createUrlParams(urlParams), { replace: true });
  }, [filters, setSearchParams, dontReplaceParams]);

  const formatStatusCellContent = ({
    status,
    cancelType,
    startDate,
    endDate,
  }: {
    status: OrderStatus;
    cancelType?: OrderCancelType | null;
    startDate: string;
    endDate: string;
  }): ReactNode => {
    const orderStatus = getOrderStatus({ status, cancelType, startDate, endDate });
    
    switch (orderStatus) {
      case 'Canceled':
        return "Canceled";
      case 'Voided':
        return "Voided";
      case 'Void Requested':
        return "Void Requested";
      case 'Scheduled':
        return (
          <>
            Scheduled
            <br />
            {formatDashDate(startDate)}
          </>
        );
      case 'Expired':
        return "Expired";
      case 'Active':
      default:
        return (
          <>
            Expires
            <br />
            {formatDashDate(endDate)}
          </>
        );
    }
  };

  const formatSensorStatusCellContent = ({
    sensorStatus,
    sensorDelivered,
    sensorActivated,
    hasTracking,
    needShipping,
    product
  }: {
    sensorStatus: SensorStatus;
    sensorDelivered: string | null;
    sensorActivated: string | null;
    hasTracking: boolean;
    needShipping?: boolean;
    product: Apps;
  }): ReactNode => {
    // If At Home order with needShipping=false, show On-Site Distribution
    if (product === Apps.AtHome && needShipping === false) {
      return "On-Site Distribution";
    }

    switch (true) {
      case sensorStatus == null:
      case sensorStatus === SensorStatus.InWarehouse:
          return "Processing";
      case sensorStatus === SensorStatus.ShippedToCustomer:
        return hasTracking ? "In Transit" : "Preparing for Shipment";
      case sensorStatus === SensorStatus.ReturnedToWarehouse:
        return "Returned";
      case sensorStatus === SensorStatus.DeliveredToCustomerButNotUsedYet:
        return (
          <>
            Delivered On
            <br />
            {formatDashDate(sensorDelivered ?? "")}
          </>
        );
      case sensorStatus === SensorStatus.AtCustomer:
        return (
          <>
            Activated On
            <br />
            {formatDashDate(sensorActivated ?? "")}
          </>
        );
      default:
        return "";
    }
  };

  const handleRenewBtnClick = (orderId: string) => {
    setLoadingToRenewId(orderId);

    OrderService.getOne({ orderId })
      .then(({ data }) => {
        // Validate essential order data before setting state
        if (data && data.patientId && data.authorizedById && Array.isArray(data.orderItems)) {
          setOrderToRenew(data);
        } else {
          setSnackbarError('Failed to load order data. Please try again.');
        }
      })
      .catch(() => {
        setSnackbarError('Failed to load order. Please try again.');
      })
      .finally(() => setLoadingToRenewId(undefined));
  };

  return (
    <>
      <div className="h-full relative">
        <TableContainer
          className={`${tableStyles.shared_table_container} ${tableStyles.padding_16_20}`}
          ref={scrollableParentRef}
        >
          <Table>
            <TableHead>
              <TableRow>
                {tableColumns.map(({ label, sortProperty }) => (
                  <TableCell key={label}>
                    {sortProperty ? (
                      <TableSortLabel
                        label={label}
                        sortProperty={sortProperty}
                        orderBy={filters._order_by}
                        onSort={onSortChange}
                      />
                    ) : (
                      label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((order, index) => {
                const {
                  id,
                  orderNum,
                  startDate,
                  endDate,
                  orderedByFname,
                  orderedByLname,
                  patientDob,
                  patientFname,
                  patientLname,
                  patientMname,
                  status,
                  cancelType,
                  sensorStatus,
                  sensorDelivered,
                  sensorActivated,
                  hasTracking,
                  product,
                  needShipping
                } = order;

                return (
                  <TableRow
                    key={orderNum}
                    ref={index === data.length - 1 ? lastElementRef : null}
                  >
                    <TableCell>{orderNum}</TableCell>
                    {showPatientColumns && (
                      <>
                        <TableCell>
                          {formatName({
                            firstName: patientFname,
                            lastName: patientLname,
                            middleName: patientMname,
                          })}
                        </TableCell>
                        <TableCell>{formatDashDate(patientDob)}</TableCell>
                      </>
                    )}
                    <TableCell>
                      {orderedByFname && orderedByLname
                        ? `${orderedByFname} ${orderedByLname}`
                        : "-"}
                    </TableCell>
                    <TableCell>{product === Apps.Remote ? "Remote" : "At Home"}</TableCell>
                    <TableCell>{formatDashDate(startDate)}</TableCell>
                    <TableCell>
                      {formatStatusCellContent({
                        status,
                        cancelType,
                        startDate,
                        endDate,
                      })}
                    </TableCell>
                    <TableCell>{product === Apps.AtHome && status === OrderStatus.Opened ? formatSensorStatusCellContent({
                        sensorStatus,
                        sensorDelivered,
                        sensorActivated,
                        hasTracking,
                        needShipping,
                        product
                      }) : ''}
                    </TableCell>
                    <TableCell className={order.canBeRenewed && !isOrderScheduled({ startDate: order.startDate }) ? "w-56" : "w-28"}>
                      <div className="flex justify-end gap-7 relative">
                        {order.canBeRenewed && !isOrderScheduled({ startDate: order.startDate }) && (
                          <>
                            {loadingToRenewId === id && (
                              <CircularProgress
                                className="absolute -left-6"
                                size={17}
                              />
                            )}

                            <button
                              className="text-pelorous enabled:hover:underline disabled:opacity-50"
                              disabled={Boolean(loadingToRenewId)}
                              onClick={() => handleRenewBtnClick(id)}
                            >
                              Renew Order
                            </button>
                          </>
                        )}

                        <Link className="text-pelorous hover:underline" to={id}>
                          View Order
                        </Link>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && !data.length && (
          <div className="mt-7">
            <NoTableItems
              icon={searchFilter ? "icon_search" : "icon_no_results"}
              message={searchFilter ? "No results found for" : noItemsMessage}
              search={searchFilter ?? undefined}
            />
          </div>
        )}

        <Loading loading={loading} />
      </div>

      {orderToRenew && (
        <CreateEditOrderModal
          isRenew
          initialOrder={orderToRenew}
          onClose={() => setOrderToRenew(undefined)}
          onCompleted={(orderId) => {
            refreshData();
            navigate(`${URLRoutes.OrdersDashboard}/${orderId}`);
          }}
          onInitialDataFetchFail={setSnackbarError}
        />
      )}

      {snackbarComponent}
    </>
  );
};

export default OrdersTable;
